/**
 * 数字人矢量人像：纯内联 SVG + CSS 渐变背景，零外部图床依赖。
 * 背景自适应任意容器比例；人物始终完整显示、底部居中。
 * 接了真实渲染引擎、把剧照传到 R2 之后，这里换成 <img> 即可。
 */

interface PortraitSpec {
  bg: [string, string];
  skin: string;
  hair: string;
  shirt: string;
  style: "long" | "short" | "curly" | "bob" | "bun";
  glasses?: boolean;
  beard?: boolean;
}

const PORTRAITS: Record<string, PortraitSpec> = {
  av_mia: {
    bg: ["#f5b8d0", "#c4a5f7"],
    skin: "#f0c39e",
    hair: "#4a3226",
    shirt: "#fdf3f7",
    style: "long",
  },
  av_jake: {
    bg: ["#8fd3f4", "#5b7cfa"],
    skin: "#e0a97e",
    hair: "#2b2320",
    shirt: "#eef4ff",
    style: "short",
    beard: true,
  },
  av_ada: {
    bg: ["#ffd8a8", "#ff8787"],
    skin: "#8d5a3b",
    hair: "#211a16",
    shirt: "#fff4ec",
    style: "curly",
  },
  av_marcus: {
    bg: ["#a0e7c4", "#3bb2a0"],
    skin: "#a9714b",
    hair: "#1d1a18",
    shirt: "#f0fbf5",
    style: "short",
    glasses: true,
  },
  av_yuki: {
    bg: ["#c3f0ff", "#9aa7ff"],
    skin: "#f4d3b3",
    hair: "#d9a441",
    shirt: "#f4f6ff",
    style: "bob",
  },
  av_lin: {
    bg: ["#ffe7a3", "#f5a35c"],
    skin: "#e6b088",
    hair: "#33261e",
    shirt: "#fffaf0",
    style: "bun",
  },
};

export function AvatarPortrait({
  avatarId,
  className,
}: {
  avatarId: string;
  className?: string;
}) {
  const c = PORTRAITS[avatarId] ?? PORTRAITS.av_mia;

  return (
    <div
      className={`relative overflow-hidden ${className ?? ""}`}
      style={{
        background: `linear-gradient(135deg, ${c.bg[0]}, ${c.bg[1]})`,
      }}
      aria-hidden="true"
    >
      {/* 背景装饰 */}
      <div className="absolute -top-6 right-[-10%] size-24 rounded-full bg-white/15" />
      <div className="absolute bottom-[-12%] left-[-8%] size-20 rounded-full bg-black/10" />

      {/* 人物：完整显示，底部居中 */}
      <svg
        viewBox="0 0 120 140"
        preserveAspectRatio="xMidYMax meet"
        className="absolute inset-0 h-full w-full"
      >
        {/* 长发/波波头/爆炸头的后层头发 */}
        {c.style === "long" && (
          <path
            d="M28 42 Q28 6 60 6 Q92 6 92 42 L94 102 Q80 112 60 112 Q40 112 26 102 Z"
            fill={c.hair}
          />
        )}
        {c.style === "bob" && (
          <path
            d="M30 40 Q30 7 60 7 Q90 7 90 40 L90 76 Q75 86 60 86 Q45 86 30 76 Z"
            fill={c.hair}
          />
        )}
        {c.style === "curly" && (
          <g fill={c.hair}>
            <circle cx="60" cy="26" r="27" />
            <circle cx="37" cy="42" r="13" />
            <circle cx="83" cy="42" r="13" />
            <circle cx="44" cy="18" r="12" />
            <circle cx="76" cy="18" r="12" />
          </g>
        )}
        {c.style === "bun" && <circle cx="60" cy="8" r="11" fill={c.hair} />}

        {/* 肩膀 / 衣服 */}
        <path
          d="M12 140 Q14 92 42 84 L60 80 L78 84 Q106 92 108 140 Z"
          fill={c.shirt}
        />

        {/* 脖子 */}
        <rect x="52" y="58" width="16" height="20" rx="7" fill={c.skin} />

        {/* 头 */}
        <ellipse cx="60" cy="40" rx="23" ry="26" fill={c.skin} />

        {/* 前层头发 */}
        {c.style === "long" && (
          <path
            d="M37 40 Q35 10 60 10 Q85 10 83 40 Q83 22 60 20 Q37 22 37 40 Z"
            fill={c.hair}
          />
        )}
        {c.style === "bob" && (
          <path
            d="M36 38 Q35 11 60 11 Q85 11 84 38 Q80 22 60 22 Q40 22 36 38 Z"
            fill={c.hair}
          />
        )}
        {(c.style === "short" || c.style === "bun") && (
          <path
            d="M36 34 Q36 10 60 10 Q84 10 84 34 Q80 18 60 18 Q40 18 36 34 Z"
            fill={c.hair}
          />
        )}
        {c.style === "curly" && (
          <path
            d="M38 32 Q40 14 60 14 Q80 14 82 32 Q76 22 60 22 Q44 22 38 32 Z"
            fill={c.hair}
          />
        )}

        {/* 胡须 */}
        {c.beard && (
          <path
            d="M42 46 Q44 66 60 66 Q76 66 78 46 Q76 60 60 60 Q44 60 42 46 Z"
            fill={c.hair}
            opacity="0.85"
          />
        )}

        {/* 眼镜 */}
        {c.glasses && (
          <g stroke="#1f1b18" strokeWidth="2.4" fill="none" opacity="0.9">
            <circle cx="50" cy="38" r="7.5" />
            <circle cx="70" cy="38" r="7.5" />
            <path d="M57.5 38 L62.5 38 M42.5 37 L38 35 M77.5 37 L82 35" />
          </g>
        )}
      </svg>
    </div>
  );
}
